import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Badge } from '@doger/ui/components/ui/badge';
import {
  Breadcrumb,
  type BreadcrumbItem,
} from '@doger/ui/components/ui/breadcrumb';
import { Card } from '@doger/ui/components/ui/card';
import { Skeleton } from '@doger/ui/components/ui/skeleton';

import { $$privateApi } from '@doger/admin/shared/api';

import { DeleteUserButton } from './delete-user-button';
import { LatestNote } from './latest-note';
import { RoleChanger } from './role-changer';
import { WhitelistToggle } from './whitelist-toggle';
import styles from './user-details.module.scss';

type UserDetailsLayoutProps = {
  children: React.ReactNode;
};

export const UserDetailsLayout = memo<UserDetailsLayoutProps>(
  ({ children }) => {
    const userId = useParams<{ userId: string }>().userId!;

    const {
      data: user,
      isLoading,
      error,
    } = useQuery({
      queryKey: ['user', userId],
      queryFn: () => $$privateApi.findOneUser({ id: userId }),
      enabled: !!userId,
      staleTime: 0,
    });

    const fullName = useMemo(() => {
      if (!user) return '';

      return user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : (user.firstName ?? user.lastName ?? 'N/A');
    }, [user]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
      () => [
        { label: 'Users', href: '/users' },
        { label: fullName ?? 'User Details' },
      ],
      [fullName]
    );

    if (isLoading) {
      return (
        <div className={styles.root}>
          <div className={styles.content}>
            <Breadcrumb items={breadcrumbItems} />
            <div className={styles.userInfo}>
              <Skeleton className={styles.skeleton} />
              <Skeleton className={styles.skeleton} />
              <Skeleton className={styles.skeleton} />
            </div>
          </div>
        </div>
      );
    }

    if (error || !user) {
      return (
        <div className={styles.root}>
          <div className={styles.content}>
            <Breadcrumb items={breadcrumbItems} />
            <div className={styles.error}>
              {error
                ? `Error loading user: ${error.message}`
                : 'User not found'}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <Breadcrumb items={breadcrumbItems} />

          <div className={styles.userInfo}>
            <Card className={styles.userCard}>
              <div className={styles.userHeader}>
                <div className={styles.userAvatar}>
                  <div className={styles.avatarPlaceholder}>
                    {user.firstName?.[0]?.toUpperCase() ??
                      user.email[0].toUpperCase()}
                  </div>
                </div>
                <div className={styles.userBasicInfo}>
                  <h1 className={styles.userName}>{fullName}</h1>
                  <p className={styles.userEmail}>{user.email}</p>

                  <div className={styles.userStatus}>
                    <Badge
                      variant={user.isActive ? 'default' : 'secondary'}
                      className={
                        user.isActive
                          ? styles.activeBadge
                          : styles.inactiveBadge
                      }
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant='outline' className={styles.roleBadge}>
                      {user.role}
                    </Badge>
                    <Badge
                      variant={user.isWhitelisted ? 'default' : 'secondary'}
                      className={
                        user.isWhitelisted
                          ? styles.whitelistedBadge
                          : styles.notWhitelistedBadge
                      }
                    >
                      {user.isWhitelisted ? 'Whitelisted' : 'Not Whitelisted'}
                    </Badge>
                  </div>
                </div>

                <div className={styles.userActions}>
                  <WhitelistToggle user={user} />
                  <DeleteUserButton user={user} />
                </div>
              </div>
            </Card>

            <div className={styles.detailsGrid}>
              <Card className={styles.detailCard}>
                <h3>Account Information</h3>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>User ID:</span>
                  <span className={styles.detailValue}>{user.id}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Role:</span>
                  <span className={styles.detailValue}>{user.role}</span>
                  <RoleChanger user={user} />
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Status:</span>
                  <span className={styles.detailValue}>
                    <Badge
                      variant={user.isActive ? 'default' : 'secondary'}
                      className={
                        user.isActive
                          ? styles.activeBadge
                          : styles.inactiveBadge
                      }
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Whitelist Status:</span>
                  <span className={styles.detailValue}>
                    <Badge
                      variant={user.isWhitelisted ? 'default' : 'secondary'}
                      className={
                        user.isWhitelisted
                          ? styles.whitelistedBadge
                          : styles.notWhitelistedBadge
                      }
                    >
                      {user.isWhitelisted ? 'Whitelisted' : 'Not Whitelisted'}
                    </Badge>
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Contact:</span>
                  <span className={styles.detailValue}>
                    {user.contact ?? 'Not provided'}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Created:</span>
                  <span className={styles.detailValue}>
                    {format(new Date(user.createdAt), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Last Updated:</span>
                  <span className={styles.detailValue}>
                    {format(new Date(user.updatedAt), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
              </Card>

              <Card className={styles.detailCard}>
                <h3>Industry</h3>

                <p>{user.industry ?? 'Not Provided'}</p>
              </Card>

              <LatestNote userId={userId} />
            </div>

            {/* Tab Content */}
            {children}
          </div>
        </div>
      </div>
    );
  }
);
